import { esNoEncontrado } from './api-base.service';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, catchError, defer, from, map, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ContactoEmergencia,
  Empleado,
  EmpleadoList,
  EmpleadoCatalogos,
  CampoExpediente,
  EmpleadoDocumento,
  Estudio,
  Pagadora,
  CreateEmpleadoRequest,
  UpdateEmpleadoRequest,
} from '@schemas/empleado.schema';
import { RhEmpleadoService } from './rh-empleado-service';

/**
 * IDs internos que se ocultan de todas las vistas (lista, autorizaciones, equipo…).
 * Son registros de prueba o placeholders que no deben mostrarse en producción.
 * Agregar o quitar IDs aquí es suficiente — no hay que tocar ninguna página.
 */
const EMPLEADOS_OCULTOS = new Set([
  'MC00500',
  'MC00501',
  'MC00502',
  'MC00503',
  'MC2222',
  'MC1234',
  'MC1235',
]);

/**
 * Director General provisional — Roberto Behar (MCA001).
 * Aún no está en la BD. Se inyecta en local/dev para que aparezca
 * en el módulo de empleados y en el flujo de autorizaciones.
 * Cuando el DBA lo agregue a la BD, esta constante deja de usarse
 * (la condición `!ya_existe` evita duplicado).
 * ID centinela -1: nunca coincidirá con un serial de PostgreSQL.
 */
const DG_PROVISIONAL_LIST: EmpleadoList = {
  id: -1,
  idInterno: 'MCA001',
  nombreCompleto: 'Roberto Behar',
  puesto: 'Director General',
  area: 'Dirección General',
  direccion: 'Dirección General',
  activo: true,
  camposFaltantes: [],
};

const DG_PROVISIONAL_DETALLE: Empleado = {
  id: -1,
  idInterno: 'MCA001',
  nombreCompleto: 'Roberto Behar',
  activo: true,
  datosLaborales: {
    direccion: 'Dirección General',
    area: 'Dirección General',
    puesto: 'Director General',
  },
};

/**
 * Empleados: servicio de dominio.
 *
 * El transporte lo hace `RhEmpleadoService`, generado desde el contrato
 * (`@intelishs-group/schema-core`): él sabe las rutas, las formas y desenvuelve
 * el `ResponseDto`. Lo que vive aquí es lo que NO está en ningún contrato —los
 * IDs ocultos, el Director General provisional, las dos cachés y sus reglas de
 * invalidación— y por eso el servicio se queda en vez de borrarse, como sí pasó
 * con el antiguo `catalog.service.ts`, que era transporte duplicado.
 *
 * La API pública sigue devolviendo `Observable`, así que los 36 call sites de
 * los 10 consumidores no se tocaron. El puente es `defer(() => from(...))`:
 * el `defer` mantiene la pereza —sin él la petición saldría al CONSTRUIR el
 * observable y no al suscribirse, y las cachés de abajo se volverían
 * precargadores—.
 *
 * Adoptar `async/await` en las páginas, como hacen los otros tres frontends, es
 * una decisión aparte que se toma una vez para todo el repo.
 */
@Injectable({ providedIn: 'root' })
export class EmpleadoService {
  private readonly api = inject(RhEmpleadoService);

  /**
   * Solo para `descargarDocumento`, que es la única llamada que NO puede pasar
   * por el generado: es una descarga binaria y el autogen la tiparía como JSON.
   * Está excluida del contrato a propósito (`excludePaths` del catálogo).
   */
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.BaseUrl}/Empleado`;

  // ── Cachés por sesión ──────────────────────────────────────────────
  // Evitan que las navegaciones repetidas vuelvan a pedir el mismo dato.
  // - getAll: se invalida tras create/update/delete/activate/changeArea (los
  //   datos cambian con cualquier mutación de empleados).
  // - getMe: NO se invalida — el empleado del usuario logueado no cambia
  //   durante la sesión.
  private getAllCache$: Observable<EmpleadoList[]> | null = null;
  private getMeCache$: Observable<Empleado | null> | null = null;
  private camposExpedienteCache$: Observable<CampoExpediente[]> | null = null;

  /** Invalida la caché de getAll. Llamar después de cualquier mutación. */
  private invalidateListCache(): void {
    this.getAllCache$ = null;
  }

  // ── GET ALL ───────────────────────────────────────────────────────
  /**
   * @param forzarRecarga descarta la caché y vuelve a pedir al backend.
   *
   * Existe porque el botón "Refrescar" del listado NO funcionaba: la caché solo
   * se invalida tras crear, editar, borrar o activar, así que al refrescar se
   * devolvía el observable cacheado y se re-emitían los mismos datos sin tocar
   * la red. Un cambio hecho por otra persona —o por el propio usuario en otra
   * pestaña— no aparecía nunca, y el botón no daba ninguna señal de que no
   * había hecho nada.
   *
   * Se llama sin `activo`, que el contrato declara opcional: sin él el backend
   * devuelve activos e inactivos, que es lo que el listado filtra por su cuenta.
   */
  getAll(forzarRecarga = false): Observable<EmpleadoList[]> {
    if (forzarRecarga) this.invalidateListCache();
    if (!this.getAllCache$) {
      this.getAllCache$ = defer(() => from(this.api.getEmpleado())).pipe(
        map((data) => {
          const lista = (data ?? []).filter((e) => !EMPLEADOS_OCULTOS.has(e.idInterno));
          // Inyectar DG provisional solo en local/dev y si aún no está en BD
          if (environment.isLocalDev) {
            const yaExiste = lista.some((e) => e.idInterno === 'MCA001');
            if (!yaExiste) lista.push(DG_PROVISIONAL_LIST);
          }
          return lista;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.getAllCache$;
  }

  // ── GET BY ID ─────────────────────────────────────────────────────
  getById(id: number): Observable<Empleado> {
    // ID centinela del DG provisional — responder sin tocar la BD
    if (id === -1) {
      return of(structuredClone(DG_PROVISIONAL_DETALLE));
    }
    return defer(() => from(this.api.getEmpleadoById(id))).pipe(map((emp) => emp!));
  }

  // ── GET ME ────────────────────────────────────────────────────────
  /**
   * Devuelve el empleado RH del usuario logueado.
   * El backend resuelve el empleado a partir del JWT actual:
   *   public.catusuario.kidusuario  →  rh.catempleadorh.fkidempleadosistema
   */
  /**
   * El empleado del usuario logueado, o `null` si su cuenta no tiene expediente.
   *
   * ── El 404 NO es un error, y por eso no se propaga como tal ──────────────
   *
   * `/api/Empleado/me` responde 404 cuando la cuenta no existe en
   * `catempleadorh`. Le pasa al superadministrador y a cualquier cuenta de
   * administración: entran a RH para trabajar sobre OTROS empleados —de ahí los
   * selectores— y no tienen expediente propio. Es la respuesta correcta, no un
   * fallo.
   *
   * Antes salía en consola como un error rojo idéntico a un 500, así que cada
   * vez que un administrador abría RH parecía que algo se había roto. Los cuatro
   * sitios que consumen esto ya hacían `emp?.id`, o sea ya esperaban la ausencia:
   * lo único que faltaba era no llamarla error.
   *
   * ⚠️ SOLO el 404 se traduce a `null`. Un 500, un timeout o un 401 siguen
   * propagándose: esos sí son fallos, y tragárselos dejaría la pantalla vacía sin
   * decir por qué — que es justo el defecto que esto viene a corregir, al revés.
   */
  getMe(): Observable<Empleado | null> {
    if (!this.getMeCache$) {
      this.getMeCache$ = defer(() => from(this.api.me())).pipe(
        map((emp) => emp ?? null),
        catchError((error: unknown) =>
          esNoEncontrado(error) ? of(null) : throwError(() => error),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.getMeCache$;
  }

  // ── CHECK INTERNAL ID ─────────────────────────────────────────────
  /**
   * Verifica si una clave interna (MC####) ya está tomada por otro empleado.
   * Usado por el validador async del formulario para advertir antes del submit.
   *
   * `excludeId` se OMITE de la petición cuando no viene —es un alta, no una
   * edición—, que es justo lo que permite `optionalQueryParams` en el namespace.
   * El escapado del valor ya no se hace a mano: `HttpParams` codifica solo.
   *
   * @param idInterno La clave a verificar (case-insensitive).
   * @param excludeId En edición, el ID del empleado actual — su propia clave
   *                  no se cuenta como duplicado.
   * @returns true si la clave ya está en uso por otro empleado, false si está libre.
   */
  checkInternalId(idInterno: string, excludeId?: number): Observable<boolean> {
    return defer(() => from(this.api.checkInternalId(idInterno, excludeId))).pipe(
      map((res) => res?.exists ?? false),
    );
  }

  // ── NEXT INTERNAL ID ──────────────────────────────────────────────
  /**
   * Sugiere el siguiente consecutivo disponible con el prefijo dado
   * (default 'MC'). El formulario lo invoca cuando el validador async
   * detecta que el ID escrito ya está en uso, para ofrecer al usuario
   * un clic-para-aplicar bajo el aviso de duplicado.
   */
  getNextInternalId(prefijo: string = 'MC'): Observable<string> {
    return defer(() => from(this.api.nextInternalId(prefijo))).pipe(
      map((res) => res?.next ?? `${prefijo}0001`),
    );
  }

  // ── CREATE ────────────────────────────────────────────────────────
  create(request: CreateEmpleadoRequest): Observable<Empleado> {
    return defer(() => from(this.api.createEmpleado(request))).pipe(
      map((emp) => emp!),
      tap(() => this.invalidateListCache()),
    );
  }

  // ── UPDATE ────────────────────────────────────────────────────────
  update(id: number, request: UpdateEmpleadoRequest): Observable<Empleado> {
    return defer(() => from(this.api.updateEmpleadoById(id, request))).pipe(
      map((emp) => emp!),
      tap(() => this.invalidateListCache()),
    );
  }

  // ── DELETE (soft delete) ──────────────────────────────────────────
  delete(id: number): Observable<void> {
    return defer(() => from(this.api.deleteEmpleadoById(id))).pipe(
      map(() => undefined),
      tap(() => this.invalidateListCache()),
    );
  }

  // ── ACTIVATE (reingreso) ────────────────────────────────────────
  activate(id: number): Observable<void> {
    return defer(() => from(this.api.activate(id))).pipe(
      map(() => undefined),
      tap(() => this.invalidateListCache()),
    );
  }

  // ── CHANGE AREA ───────────────────────────────────────────────────
  /**
   * Cambia área, dirección y director (superior) de un empleado en un solo
   * request. Hoy el backend hace 2 UPDATEs raw SQL (relempleadodireccionarea
   * y movempleadojerarquia) — sin histórico. Invalida la caché del listado
   * para que el siguiente reload muestre los nuevos valores.
   *
   * Se conservan los cuatro argumentos sueltos en vez de exponer el
   * `ChangeAreaRequest` del contrato: son la firma que usa el modal y armar el
   * objeto es trabajo de esta capa.
   *
   * @param superiorId  ID del nuevo director. 0 = Director General
   *                    (convención del DBA: kidempleadorh = 0).
   */
  changeArea(
    id: number,
    areaId: number,
    direccionId: number,
    superiorId: number,
  ): Observable<Empleado> {
    return defer(() => from(this.api.area(id, { areaId, direccionId, superiorId }))).pipe(
      map((emp) => emp!),
      tap(() => this.invalidateListCache()),
    );
  }

  // ── CATÁLOGOS ─────────────────────────────────────────────────────
  /**
   * Áreas, puestos y direcciones distintas de los empleados en BD.
   *
   * El fallback devuelve las tres listas vacías para que el formulario y el
   * modal de cambio de área abran igual si el endpoint falla, en vez de romper
   * la pantalla. Los dos solo leen `areas` y `direcciones`.
   */
  /**
   * Los campos del expediente que el listado puede reportar en
   * `camposFaltantes`, con su etiqueta. La lista es del backend: aquí no hay
   * copia, para que el día que un campo entre o salga no haya dos versiones.
   *
   * Se cachea por sesión porque es estática del lado del servidor. Si falla,
   * degrada a lista vacía: el listado se ve igual, solo sin chips de faltantes.
   */
  getCamposExpediente(): Observable<CampoExpediente[]> {
    this.camposExpedienteCache$ ??= defer(() => from(this.api.expediente_campos())).pipe(
      map((campos) => campos ?? []),
      catchError(() => {
        this.camposExpedienteCache$ = null;
        return of([]);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.camposExpedienteCache$;
  }

  getCatalogos(): Observable<EmpleadoCatalogos> {
    return defer(() => from(this.api.catalogos())).pipe(
      map((cat) => cat ?? { areas: [], puestos: [], direcciones: [] }),
    );
  }

  // ── ESTUDIOS (CRUD por fila) ──────────────────────────────────────

  /** Alta de un estudio. Devuelve el estudio con `id` (kidempleadoestudio). */
  crearEstudio(empleadoId: number, dto: Estudio): Observable<Estudio> {
    return defer(() => from(this.api.createEstudios(empleadoId, dto))).pipe(map((r) => r!));
  }

  /** Actualiza un estudio existente. */
  actualizarEstudio(empleadoId: number, kidEstudio: number, dto: Estudio): Observable<Estudio> {
    return defer(() => from(this.api.updateEstudios(empleadoId, kidEstudio, dto))).pipe(
      map((r) => r!),
    );
  }

  /** Baja lógica (bactivo=false) de un estudio. */
  eliminarEstudio(empleadoId: number, kidEstudio: number): Observable<void> {
    return defer(() => from(this.api.deleteEstudios(empleadoId, kidEstudio))).pipe(
      map(() => undefined),
    );
  }

  // ── PAGADORAS IMSS (CRUD por fila) ────────────────────────────────

  /** Alta de una pagadora. Devuelve la pagadora con `id` (kidpagadora). */
  crearPagadora(empleadoId: number, dto: Pagadora): Observable<Pagadora> {
    return defer(() => from(this.api.createPagadoras(empleadoId, dto))).pipe(map((r) => r!));
  }

  /** Actualiza una pagadora existente. */
  actualizarPagadora(empleadoId: number, kidPagadora: number, dto: Pagadora): Observable<Pagadora> {
    return defer(() => from(this.api.updatePagadoras(empleadoId, kidPagadora, dto))).pipe(
      map((r) => r!),
    );
  }

  // ── CONTACTOS DE EMERGENCIA (CRUD por fila) ───────────────────────
  // Cada fila se persiste de forma independiente desde el form. El backend
  // calcula inocontacto (MAX+1) y aplica soft-delete con bactivo=false.

  /** Alta de un contacto. Devuelve el contacto con `id` (kidempleadocontacto). */
  crearContacto(empleadoId: number, dto: ContactoEmergencia): Observable<ContactoEmergencia> {
    return defer(() => from(this.api.createContactos(empleadoId, dto))).pipe(map((r) => r!));
  }

  /** Actualiza un contacto existente (nombre/parentesco/teléfono). */
  actualizarContacto(
    empleadoId: number,
    kidContacto: number,
    dto: ContactoEmergencia,
  ): Observable<ContactoEmergencia> {
    return defer(() => from(this.api.updateContactos(empleadoId, kidContacto, dto))).pipe(
      map((r) => r!),
    );
  }

  /** Baja lógica (bactivo=false) de un contacto. */
  eliminarContacto(empleadoId: number, kidContacto: number): Observable<void> {
    return defer(() => from(this.api.deleteContactos(empleadoId, kidContacto))).pipe(
      map(() => undefined),
    );
  }

  // ── DOCUMENTOS (FileManager / S3) ─────────────────────────────────
  // El backend proxquea al microservicio FileManager (sube a S3) y guarda la
  // referencia { tipo, kiddocumento } en catempleadorh.jdatos.archivos.

  /** Lista los documentos (referencias) del empleado. */
  getDocumentos(empleadoId: number): Observable<EmpleadoDocumento[]> {
    return defer(() => from(this.api.getDocumentos(empleadoId))).pipe(map((docs) => docs ?? []));
  }

  /**
   * Sube un documento (multipart) al backend, que lo reenvía al FileManager.
   * Devuelve la lista actualizada de documentos del empleado.
   *
   * El `FormData` se arma aquí y no en el generado: el contrato solo declara que
   * el endpoint recibe multipart, no qué campos lleva.
   *
   * OJO con el `.object`: este método es la excepción del servicio generado. Las
   * subidas multipart no pasan por `ApiBaseService` —el generador las emite con
   * `lastValueFrom(this.http.post(...))` directo—, así que devuelven el
   * `ResponseDto` CRUDO en vez del contenido ya desenvuelto. Es el único punto
   * donde hay que sacar el `.object` a mano, y lo señaló el compilador.
   */
  subirDocumento(empleadoId: number, tipo: number, file: File): Observable<EmpleadoDocumento[]> {
    const form = new FormData();
    form.append('tipo', String(tipo));
    form.append('file', file, file.name);
    return defer(() => from(this.api.createDocumentos(empleadoId, form))).pipe(
      map((res) => res?.object ?? []),
    );
  }

  /** Elimina (borrado lógico) un documento y devuelve la lista actualizada. */
  eliminarDocumento(empleadoId: number, kidDocumento: number): Observable<EmpleadoDocumento[]> {
    return defer(() => from(this.api.deleteDocumentos(empleadoId, kidDocumento))).pipe(
      map((docs) => docs ?? []),
    );
  }

  /**
   * Descarga el contenido binario de un documento.
   *
   * ÚNICA llamada que sigue por `HttpClient`, y a propósito: el endpoint está
   * excluido del contrato (`excludePaths`) porque el autogen lo tiparía como
   * JSON y rompería el blob.
   *
   * Devuelve la `HttpResponse` completa (no solo el Blob) porque el nombre real
   * del archivo viaja en el header `Content-Disposition`. Requiere que el backend
   * lo publique con `WithExposedHeaders("Content-Disposition")`; sin eso el
   * navegador lo oculta y el consumidor cae al nombre del tipo de documento.
   */
  descargarDocumento(empleadoId: number, kidDocumento: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/${empleadoId}/documentos/${kidDocumento}/contenido`, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
